/**
 * "Return to intended destination" after login (anon-gating phase 2).
 *
 * A logged-out user who follows a deep link into a gated route (e.g. a shared
 * duel, profile, or track) gets bounced to /login by the App.svelte auth
 * guard. We stash the attempted destination here so it can be restored after
 * a successful login/signup/OAuth flow — including one that first detours
 * through /onboarding for a brand-new user.
 *
 * MUST use sessionStorage, NOT a module-level variable or Svelte `$state` —
 * the OAuth flows navigate away via `window.location.href` to the provider
 * and back via a full-page load, which wipes any in-memory JS state.
 * sessionStorage survives that round-trip (same tab; cleared on tab close).
 */

const RETURN_TO_KEY = 'bd_return_to';

// Auth/landing pages are never a meaningful "return to" destination — capturing
// one would just bounce the user back to the login/onboarding flow they're
// already in. Kept as bare paths (no query) since callers pass/compare paths
// stripped of query for this check.
const EXCLUDED_PATHS = new Set([
  '/login',
  '/register',
  '/forgot',
  '/reset-password',
  '/verify-email',
  '/',
  '/onboarding',
]);

/**
 * True when `path` is safe to store/honor as an internal post-login redirect
 * target: a same-app hash path starting with exactly one `/` — never a
 * protocol-relative (`//`) or absolute (`scheme://` / `http…`) URL — and not
 * one of the auth/landing pages.
 *
 * This re-validates on *read* (see `consumeReturnTo` below) because the
 * threat model includes a tampered sessionStorage value, not just a value
 * this module itself wrote — so the extra hardening below (backslash /
 * percent-encoded-slash rejection) is defense-in-depth against a value that
 * bypassed `captureReturnTo`.
 */
function isSafeReturnPath(path: string): boolean {
  if (!path) return false;
  if (!path.startsWith('/') || path.startsWith('//')) return false;
  // A backslash directly after the leading slash (`/\evil.com`) — some
  // browsers/consumers normalize a leading `\` to `/`, turning this into the
  // protocol-relative `//evil.com` the check above is meant to block.
  if (path[1] === '\\') return false;
  if (path.includes('://')) return false;
  // Percent-encoded slash (`%2f`/`%2F`) could smuggle a scheme/host past the
  // literal-character checks above once decoded downstream.
  if (/%2f/i.test(path)) return false;
  // Percent-encoded backslash (`%5c`/`%5C`) — symmetric to the raw-backslash
  // check above: some consumers decode-then-normalize `\` to `/`, so this can
  // smuggle the same `/\evil.com` → `//evil.com` protocol-relative escape.
  if (/%5c/i.test(path)) return false;
  // (No `^https?` check needed here — anything not starting with `/` already
  // returned false above, so a bare `http…` value can never reach this line.)
  const bare = path.split('?')[0];
  return !EXCLUDED_PATHS.has(bare);
}

/**
 * Stash the attempted destination before redirecting a logged-out user to
 * /login. No-ops silently for auth/landing pages and anything that fails the
 * open-redirect check (see isSafeReturnPath) — there's nothing worth
 * remembering in those cases.
 */
export function captureReturnTo(path: string): void {
  if (!isSafeReturnPath(path)) return;
  try {
    sessionStorage.setItem(RETURN_TO_KEY, path);
  } catch {
    // sessionStorage unavailable (private mode / storage disabled) — skip capture.
  }
}

/**
 * Read + clear the stashed return-to destination, re-validating it (defends
 * against a tampered or stale stored value). Returns null when absent, empty,
 * or invalid — callers should fall back to a default landing route in that case.
 */
export function consumeReturnTo(): string | null {
  try {
    const value = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
    return value && isSafeReturnPath(value) ? value : null;
  } catch {
    return null;
  }
}
